import { useEffect, useState } from "react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { ejemploParaMes, EJEMPLO_GIRO } from "@/modules/analisis_financiero/ejemploCarga";
import {
  descargarProyeccion,
  generarProyeccion,
  guardarEstado,
  listarEstados,
  obtenerProyeccionConfig,
  previsualizarArchivo,
  previsualizarManual,
} from "@/modules/analisis_financiero/analisisFinancieroApi";
import { moneyMx } from "@/modules/analisis_financiero/semaforoUi";

const MESES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

function blobDescarga(data, nombre) {
  const url = URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = url;
  a.download = nombre;
  a.click();
  URL.revokeObjectURL(url);
}

export default function ProyectadoCierreTab({
  clientId,
  ejercicio,
  setEjercicio,
  mes,
  rubros,
  puedeEscribir,
  setGiro,
}) {
  const [ultimoMes, setUltimoMes] = useState(mes || 9);
  const [resultado, setResultado] = useState(null);
  const [congelado, setCongelado] = useState(false);
  const [mesesCargados, setMesesCargados] = useState([]);
  const [texto, setTexto] = useState("");
  const [generando, setGenerando] = useState(false);
  const [preview, setPreview] = useState(null);

  const recargarMeses = async (anio = ejercicio) => {
    if (!clientId) {
      setMesesCargados([]);
      return;
    }
    try {
      const { data } = await listarEstados({ clientId, ejercicio: Number(anio) });
      const meses = [...new Set((data?.renglones || []).map((r) => Number(r.mes)))].sort((a, b) => a - b);
      setMesesCargados(meses);
    } catch {
      setMesesCargados([]);
    }
  };

  useEffect(() => {
    setUltimoMes(mes || 9);
  }, [mes]);

  useEffect(() => {
    if (!clientId) {
      setResultado(null);
      setCongelado(false);
      return;
    }
    let vivo = true;
    obtenerProyeccionConfig({ clientId, ejercicio: Number(ejercicio) })
      .then(({ data }) => {
        if (!vivo) return;
        setCongelado(Boolean(data?.congelado));
        setResultado(data?.resultado?.rubros ? data.resultado : null);
        if (data?.ultimo_mes_real) setUltimoMes(Number(data.ultimo_mes_real));
      })
      .catch(() => {
        if (vivo) setResultado(null);
      });
    recargarMeses(ejercicio);
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, ejercicio]);

  const guardarPreview = async (data, origen) => {
    if (!puedeEscribir || !clientId) return;
    const renglones = (data?.renglones || []).map((row) => ({
      etiqueta_original: row.etiqueta_original,
      importe: row.importe,
      mes: row.mes || Number(ultimoMes),
      rubro: row.ignorar ? "ignorar" : row.rubro,
      ignorar: Boolean(row.ignorar) || row.rubro === "ignorar",
    }));
    await guardarEstado({
      client_id: clientId,
      ejercicio: Number(ejercicio),
      mes: Number(ultimoMes),
      tipo: "real",
      origen,
      renglones,
    });
    toast.success("Estado de resultados guardado. Ya puede generar la proyección.");
    setPreview(null);
    await recargarMeses();
  };

  const onArchivo = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !puedeEscribir) return;
    try {
      const { data } = await previsualizarArchivo({ file, clientId });
      if (data?.ok === false) {
        toast.error(data.mensaje || "No se pudo leer el archivo.");
        return;
      }
      setPreview({ ...data, origen: data.formato === "pdf" ? "pdf" : "excel" });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "No se pudo leer el archivo.");
    }
  };

  const onEjemplo = () => {
    if (!puedeEscribir) return;
    if (setGiro) setGiro(EJEMPLO_GIRO);
    setPreview({
      origen: "manual",
      renglones: ejemploParaMes(ultimoMes),
      advertencias: ["Cifras de ejemplo, solo para ver el flujo."],
    });
  };

  const generar = async () => {
    if (!puedeEscribir || !clientId || congelado) return;
    setGenerando(true);
    try {
      const { data } = await generarProyeccion({
        client_id: clientId,
        ejercicio: Number(ejercicio),
        ultimo_mes_real: Number(ultimoMes),
      });
      setResultado(data?.resultado || null);
      toast.success("Proyección generada hasta diciembre.");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "No se pudo generar la proyección.");
    } finally {
      setGenerando(false);
    }
  };

  const descargar = async (formato) => {
    if (!clientId || !resultado) return;
    try {
      const { data } = await descargarProyeccion(formato, {
        clientId,
        ejercicio: Number(ejercicio),
      });
      blobDescarga(data, `proyeccion-${ejercicio}.${formato}`);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "No se pudo descargar.");
    }
  };

  const mesesCabecera = resultado?.meses || MESES.map((etiqueta, i) => ({
    mes: i + 1,
    etiqueta,
    tipo: i + 1 <= Number(ultimoMes) ? "real" : "estimado",
  }));

  return (
    <div className="space-y-4" data-testid="tab-proyectado-cierre">
      <div className="rounded-sm border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700 space-y-1">
        <p className="font-semibold text-[#0F2D5C]">Proyectado al cierre</p>
        <p className="text-xs">
          Indique el ejercicio y el último mes con cifras reales. Los meses que siguen hasta diciembre se estiman
          con el método de cada rubro definido en Configuración de datos. El tope de variación es de −100 % a +200 %.
        </p>
      </div>

      {congelado ? (
        <p className="rounded-sm border border-amber-300 bg-amber-50 px-3 py-2 text-sm" data-testid="escenario-congelado">
          Este escenario ya se adjuntó al expediente y quedó congelado. Puede verlo y descargarlo, no generarlo de nuevo.
        </p>
      ) : null}

      <div className="grid gap-3 md:grid-cols-3" data-testid="corte-proyeccion">
        <label className="text-xs">Ejercicio
          <input
            type="number"
            min={2000}
            max={2100}
            className="mt-1 w-full rounded-sm border px-2 py-1.5 text-sm"
            value={ejercicio}
            onChange={(e) => setEjercicio(Number(e.target.value))}
            data-testid="proyeccion-ejercicio"
          />
        </label>
        <label className="text-xs">Último mes real
          <select
            className="mt-1 w-full rounded-sm border px-2 py-1.5 text-sm"
            value={ultimoMes}
            onChange={(e) => setUltimoMes(Number(e.target.value))}
            data-testid="proyeccion-ultimo-mes"
          >
            {MESES.map((nombre, i) => (
              <option key={nombre} value={i + 1}>{nombre}</option>
            ))}
          </select>
        </label>
        <p className="text-xs self-end text-slate-600" data-testid="meses-cargados">
          Meses cargados en {ejercicio}: {mesesCargados.length
            ? mesesCargados.map((n) => MESES[n - 1]).join(", ")
            : "ninguno"}
        </p>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="carga-proyeccion">
        <label className={`rounded-sm border px-3 py-1.5 text-xs ${puedeEscribir ? "cursor-pointer" : "opacity-50"}`}>
          Cargar Excel o PDF
          <input
            type="file"
            accept=".xlsx,.xls,.pdf"
            className="hidden"
            disabled={!puedeEscribir || congelado}
            onChange={onArchivo}
            data-testid="proyeccion-archivo"
          />
        </label>
        <button type="button" className="rounded-sm border px-3 py-1.5 text-xs disabled:opacity-50" disabled={!puedeEscribir || congelado} onClick={onEjemplo} data-testid="proyeccion-ejemplo">
          Cargar ejemplo
        </button>
      </div>
      <div className="space-y-2">
        <textarea
          className="w-full min-h-20 rounded-sm border px-2 py-1 text-sm"
          placeholder={"Ventas netas\t1000000\nSueldos y salarios\t350000"}
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          disabled={!puedeEscribir || congelado}
          data-testid="proyeccion-texto"
        />
        <button
          type="button"
          className="rounded-sm border px-3 py-1.5 text-xs disabled:opacity-50"
          disabled={!puedeEscribir || congelado}
          onClick={async () => {
            try {
              const { data } = await previsualizarManual({ clientId, texto });
              if (data?.ok === false) {
                toast.error(data.mensaje || "No se reconoció el pegado.");
                return;
              }
              setPreview({ ...data, origen: "manual" });
            } catch (err) {
              toast.error(formatApiError(err?.response?.data?.detail) || "No se reconoció el pegado.");
            }
          }}
        >
          Previsualizar pegado
        </button>
      </div>

      {preview?.renglones?.length ? (
        <div className="rounded-sm border p-3 text-xs space-y-2" data-testid="proyeccion-preview">
          <p>Vista previa: {preview.renglones.length} renglones. Se guardan como reales del ejercicio {ejercicio}.</p>
          {(preview.advertencias || []).map((msg) => <p key={msg} className="text-amber-900">{msg}</p>)}
          <button
            type="button"
            className="rounded-sm bg-[#0F2D5C] px-3 py-1.5 text-xs font-semibold text-white"
            onClick={() => guardarPreview(preview, preview.origen || "manual").catch((err) => {
              toast.error(formatApiError(err?.response?.data?.detail) || "No se pudo guardar.");
            })}
          >
            Guardar estos renglones
          </button>
        </div>
      ) : null}

      <button
        type="button"
        className="rounded-sm bg-[#0F2D5C] px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
        disabled={!puedeEscribir || !clientId || congelado || generando}
        onClick={generar}
        data-testid="btn-generar-proyeccion"
      >
        {generando ? "Generando…" : "Generar proyección"}
      </button>

      {resultado?.rubros?.length ? (
        <section className="space-y-2" data-testid="resultado-proyeccion">
          <div className="flex flex-wrap gap-2">
            <button type="button" className="rounded-sm border px-3 py-1.5 text-xs" onClick={() => descargar("pdf")} data-testid="btn-descargar-pdf">
              Descargar PDF
            </button>
            <button type="button" className="rounded-sm border px-3 py-1.5 text-xs" onClick={() => descargar("xlsx")} data-testid="btn-descargar-xlsx">
              Descargar Excel
            </button>
          </div>
          <p className="text-xs text-slate-600">{resultado.leyenda}</p>
          <div className="overflow-x-auto rounded-sm border border-slate-200">
            <table className="min-w-full text-xs">
              <thead>
                <tr>
                  <th className="px-2 py-2 text-left bg-slate-50">Rubro</th>
                  {mesesCabecera.map((m) => (
                    <th
                      key={m.mes}
                      className={`px-2 py-2 text-right ${m.tipo === "estimado" ? "bg-amber-100 text-amber-950" : "bg-white"}`}
                    >
                      {m.etiqueta}
                      <div className="font-normal">{m.tipo === "estimado" ? "Estimado" : "Real"}</div>
                    </th>
                  ))}
                  <th className="px-2 py-2 text-right bg-slate-50">Año</th>
                </tr>
              </thead>
              <tbody>
                {resultado.rubros.map((rubro) => (
                  <tr key={rubro.renglon_id} className="border-t border-slate-100" data-testid={`proy-${rubro.renglon_id}`}>
                    <td className="px-2 py-2">
                      <div className="font-medium">{rubro.etiqueta}</div>
                    </td>
                    {(rubro.meses || []).map((celda) => (
                      <td
                        key={celda.mes}
                        className={`px-2 py-2 text-right ${celda.tipo === "estimado" ? "bg-amber-50 italic text-amber-950" : "bg-white"}`}
                      >
                        {celda.importe === null || celda.importe === undefined ? "—" : moneyMx(celda.importe)}
                      </td>
                    ))}
                    <td className="px-2 py-2 text-right font-semibold">{moneyMx(rubro.total_anio)}</td>
                  </tr>
                ))}
                <tr className="border-t border-slate-300 bg-slate-50 font-semibold" data-testid="proy-utilidad">
                  <td className="px-2 py-2">Utilidad o pérdida</td>
                  {(resultado.totales_mes || []).map((celda) => (
                    <td
                      key={celda.mes}
                      className={`px-2 py-2 text-right ${celda.tipo === "estimado" ? "bg-amber-100 italic" : ""}`}
                    >
                      {moneyMx(celda.utilidad)}
                    </td>
                  ))}
                  <td className="px-2 py-2 text-right">{moneyMx(resultado.utilidad_anio)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          {(resultado.notas || []).length ? (
            <ul className="text-xs text-slate-600 list-disc pl-4">
              {resultado.notas.map((nota) => <li key={nota}>{nota}</li>)}
            </ul>
          ) : null}
          <p className="text-xs text-slate-500">
            Variación mostrada en notas. Un recorte queda dentro de −100 % a +200 %.
          </p>
        </section>
      ) : (
        <p className="text-sm text-slate-500" data-testid="proyeccion-vacia">
          Todavía no hay una proyección de este ejercicio. Cargue o elija los meses reales y pulse Generar proyección.
        </p>
      )}
    </div>
  );
}
